import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { LabellingCostDailyEntry } from "src/store/api/generatedApi";

type LabellingCostTrendChartProps = Readonly<{
  entries: LabellingCostDailyEntry[];
  isLoading: boolean;
}>;

export const LabellingCostTrendChart = ({
  entries,
  isLoading,
}: LabellingCostTrendChartProps) => {
  const { t } = useTenantTranslation();
  const { data: palette } = useTheme().palette;

  const { dates, labellingRatios, reviewRatios } = useMemo(() => {
    const byDate = sorted([...entries], (entry) => entry.date);
    return {
      dates: byDate.map((entry) => entry.date),
      labellingRatios: byDate.map((entry) => entry.labelling_cost_ratio),
      reviewRatios: byDate.map((entry) => entry.review_cost_ratio),
    };
  }, [entries]);

  const traces: Partial<PlotData>[] = useMemo(
    () => [
      {
        x: dates,
        y: labellingRatios,
        type: "bar",
        name: t("labelling"),
        marker: { color: palette.blue },
      },
      {
        x: dates,
        y: reviewRatios,
        type: "bar",
        name: t("reviewing"),
        marker: { color: palette.orange },
      },
    ],
    [dates, labellingRatios, reviewRatios, t, palette]
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (dates.length === 0) {
    return <Alert severity="info">{t("noDataAvailable")}</Alert>;
  }

  return (
    <Plot
      data={traces}
      layout={{
        autosize: true,
        barmode: "group",
        xaxis: { type: "date" },
        yaxis: { title: { text: `${t("labelHoursPerHour")} (h/h)` } },
        margin: { l: 60, r: 30, t: 30, b: 60 },
        hovermode: "x unified",
        plot_bgcolor: "transparent",
        paper_bgcolor: "transparent",
      }}
      config={{
        responsive: true,
        displayModeBar: false,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "350px" }}
      useResizeHandler
    />
  );
};
